import { ErrorBoundary } from "@/lib/bugsnag/client";
import type { AppProps } from "next/app";

import "@/styles/globals.css";
import { useState, useEffect } from "react";
import { UserProvider } from "@/providers/user-provider";
import { UiProvider } from "@/providers/ui-provider";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Head from "next/head";
import { useRouter } from "next/router";
import { TawkWidget } from "@/components/tawk-widget";
import { TawkAuth } from "@/components/tawk-auth";
import { FpjsProvider } from "@fingerprintjs/fingerprintjs-pro-react";
import { FingerprintInit } from "@/components/fingerprint-init";
import { NuqsAdapter } from "nuqs/adapters/next/pages";

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  // Dev-only: log route-change timing pra investigar lentidão.
  // Remover/desligar se ficar barulhento.
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    let startedAt = 0;
    let from = "";
    const onStart = (_url: string) => {
      startedAt = performance.now();
      // URL ainda não mudou no `routeChangeStart` — pega a anterior.
      from = window.location.pathname + window.location.search;
    };
    const onComplete = (url: string) => {
      const nav = performance.now() - startedAt;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const paint = performance.now() - startedAt;
          // eslint-disable-next-line no-console
          console.log(
            `[route] ${from} → ${url}: nav=${nav.toFixed(0)}ms paint=${paint.toFixed(0)}ms`,
          );
        });
      });
    };
    const onError = (err: Error, url: string) => {
      const nav = performance.now() - startedAt;
      // eslint-disable-next-line no-console
      console.warn(`[route] error ${from} → ${url} after ${nav.toFixed(0)}ms`, err);
    };
    router.events.on("routeChangeStart", onStart);
    router.events.on("routeChangeComplete", onComplete);
    router.events.on("routeChangeError", onError);
    return () => {
      router.events.off("routeChangeStart", onStart);
      router.events.off("routeChangeComplete", onComplete);
      router.events.off("routeChangeError", onError);
    };
  }, [router.events]);
  const SITE_NAME = "ViOux";
  const routeTitleMap: Record<string, string> = {
    "/": "Dashboard",
    "/login": "Login",
    "/admin": "Dashboard",
    "/admin/clientes": "Clientes",
    "/admin/lojas": "Lojas",
    "/admin/maquininhas": "Maquininhas",
    "/admin/datasync": "Sincronização",
    "/admin/transacoes": "Transações",
    "/admin/dispositivos": "Dispositivos",
    "/transacoes": "Transações",
    "/beneficiarios": "Beneficiários",
    "/admin/beneficiarios": "Beneficiários",
  };
  const componentWithTitle = Component as AppProps["Component"] & {
    title?: string;
  };
  let baseTitle = componentWithTitle.title ?? routeTitleMap[router.pathname];
  if (!baseTitle && router.pathname.startsWith("/admin/client/")) {
    baseTitle = "Cliente";
  }
  baseTitle = baseTitle ?? "Dashboard";
  const fullTitle = `${baseTitle} | ${SITE_NAME}`;

  // FpjsProvider precisa ser GLOBAL: /login usa o visitorId no fluxo de login
  // (registro de device). Só o touch (FingerprintInit) é pulado no /login —
  // deslogado ele seria um 401 inútil.
  const isLoginRoute = router.pathname === "/login";

  // Profile vindo do SSR (requireClientAuth/requireAdminAuth) — hidrata o
  // UserProvider sem re-fetch de auth+clients no browser.
  const ssrProfile =
    (pageProps as { auth?: { profile?: import("@/providers/user-provider").ClientProfile | null } })
      .auth?.profile ?? null;

  const app = (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <UiProvider>
          <UserProvider initialClient={ssrProfile}>
            <Head>
              <title>{fullTitle}</title>
            </Head>
            <Component {...pageProps} />
            {!isLoginRoute && <FingerprintInit />}
            <TawkWidget />
            <TawkAuth />
            <Toaster />
          </UserProvider>
        </UiProvider>
      </NuqsAdapter>
    </QueryClientProvider>
  );

  return (
    <ErrorBoundary>
      <FpjsProvider
        loadOptions={{
          apiKey: process.env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY || "",
        }}
      >
        {app}
      </FpjsProvider>
    </ErrorBoundary>
  );
}
